import { Injectable } from '@angular/core';
import { firstValueFrom, from, Observable, of, switchMap } from 'rxjs';
import { BaseEntityService } from './base-entity';
import { StorageService } from './storage';
import { BaseApiService } from './base-api-service';

export interface RouteMembershipSummary {
  id?: number;
  routeId: number;
  route: string;
  segmentId: number;
  segment: string;
  isPrimary: boolean;
  visitDay?: string;
  sequence?: number;
}

export interface Customer {
  id: number;
  clientCustomerUuid?: string;
  name: string;
  phone: string;
  mobile?: string;
  whatsappNumber?: string;
  latitude: string | number;
  longitude: string | number;
  gpsAccuracyM?: number;
  gpsCapturedAt?: string;
  gpsSource?: string;
  country: string;
  countryId?: number | null;
  state?: string;
  stateId?: number | null;
  gstTreatment?: string;
  tax_id: string | false;
  vat?: string;
  street?: string;
  street2?: string;
  city?: string;
  zip?: string;
  categories: string[];
  categoryIds?: number[];
  image: string;
  imageMimeType?: string;
  priceList: string;
  priceListId: number;
  outstandingBalance?: number;
  openInvoiceCount?: number;
  currency?: string;
  route: string;
  /** Backward-compatible primary segment id. */
  routeId: number;
  /** All segment ids that make this customer available on selected routes. */
  routeIds?: number[];
  routeMemberships?: RouteMembershipSummary[];
  linkedMasterCustomerId?: number | null;

  // Local/offline sync metadata. Never sent as Odoo business fields.
  pendingSync?: boolean;
  photoSyncPending?: boolean;
  pendingRouteSegmentIds?: number[];
  localOnly?: boolean;
}

export interface CustomerCreateInput {
  clientCustomerUuid: string;
  name: string;
  phone?: string;
  mobile?: string;
  whatsappNumber?: string;
  vat?: string;
  street?: string;
  street2?: string;
  city?: string;
  zip?: string;
  countryId?: number | null;
  stateId?: number | null;
  gstTreatment?: string;
  tagIds?: number[];
  tagNames?: string[];
  latitude?: number | null;
  longitude?: number | null;
  gpsAccuracyM?: number | null;
  gpsCapturedAt?: string | null;
  routeId?: number | null;
  routeSegmentId?: number | null;
  routeName?: string;
  segmentName?: string;
  priceListId?: number | null;
  priceListName?: string;
  image?: string;
  imageMimeType?: string;
}

export interface CustomerStateOption {
  id: number;
  name: string;
  code: string;
  countryId: number;
}

export interface CustomerTagOption {
  id: number;
  name: string;
}

export interface GstTreatmentOption {
  value: string;
  label: string;
  requiresVat: boolean;
}

export interface CustomerCreateOptions {
  countryId: number | null;
  countryName: string;
  defaultStateId: number | null;
  states: CustomerStateOption[];
  tags: CustomerTagOption[];
  gstTreatments: GstTreatmentOption[];
  defaultGstTreatment: string;
}

export interface GstLookupCustomer {
  name?: string;
  phone?: string;
  street?: string;
  street2?: string;
  city?: string;
  zip?: string;
  vat?: string;
  stateId?: number | null;
  stateName?: string;
  countryId?: number | null;
  countryName?: string;
}

export interface GstLookupResponse {
  status: 'success' | 'error';
  found?: boolean;
  message?: string;
  customer?: GstLookupCustomer;
}

export interface CustomerDuplicateMatch {
  score: number;
  distanceM: number | null;
  reasons: string[];
  customer: Customer;
}

interface CustomerCreateResponse {
  status: 'success' | 'error';
  message?: string;
  customer?: Customer;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerService extends BaseEntityService<Customer> {
  private readonly customerDbName = 'CustomerDatabase';
  private readonly customerStoreName = 'customers';
  private readonly createOptionsCacheKey = 'a1-route-sales.customer-create-options.v2';

  constructor(
    indexedDbService: StorageService<Customer>,
    baseApi: BaseApiService
  ) {
    super(baseApi, indexedDbService, 'CustomerDatabase', 'customers', 'customers');
  }

  async getCreateOptions(): Promise<CustomerCreateOptions> {
    const readCached = (): CustomerCreateOptions | null => {
      try {
        const raw = localStorage.getItem(this.createOptionsCacheKey);
        return raw ? JSON.parse(raw) as CustomerCreateOptions : null;
      } catch {
        return null;
      }
    };

    if (navigator.onLine) {
      try {
        const response = await firstValueFrom(
          this.baseApi.post<{ status: string; message?: string; options?: CustomerCreateOptions }>('customers/form-options', {})
        );
        if (response.status === 'success' && response.options) {
          try {
            localStorage.setItem(this.createOptionsCacheKey, JSON.stringify(response.options));
          } catch {
            // Cache failure must not block customer creation.
          }
          return response.options;
        }
        throw new Error(response.message || 'Customer form options are unavailable.');
      } catch (err) {
        const cached = readCached();
        if (cached) return cached;
        throw err;
      }
    }

    const cached = readCached();
    if (cached) return cached;
    throw new Error('Customer form options are not cached. Connect to the server once before creating customers offline.');
  }

  async lookupGstin(gstin: string): Promise<GstLookupResponse> {
    if (!navigator.onLine) {
      throw new Error('GST lookup requires an internet connection.');
    }
    return await firstValueFrom(
      this.baseApi.post<GstLookupResponse>('customers/gst-lookup', { gstin })
    );
  }

  override get(forced = false): Observable<Customer[]> {
    if (!forced) {
      return super.get(false);
    }

    // Preserve offline/pending records when a server refresh replaces the local customer cache.
    return from(this.storageService.getAll(this.customerDbName, this.customerStoreName)).pipe(
      switchMap(localBeforeRefresh => super.get(true).pipe(
        switchMap(serverCustomers => {
          const localByUuid = new Map(
            localBeforeRefresh
              .filter(c => !!c.clientCustomerUuid)
              .map(c => [c.clientCustomerUuid as string, c])
          );

          const merged = serverCustomers.map(server => {
            const local = server.clientCustomerUuid ? localByUuid.get(server.clientCustomerUuid) : undefined;
            if (!local) return server;
            localByUuid.delete(server.clientCustomerUuid as string);
            return {
              ...server,
              image: server.image || local.image,
              imageMimeType: server.image ? (server.imageMimeType || 'image/jpeg') : local.imageMimeType,
              photoSyncPending: local.photoSyncPending,
              pendingRouteSegmentIds: local.pendingRouteSegmentIds,
            };
          });

          for (const local of localByUuid.values()) {
            if (local.pendingSync || local.localOnly || local.photoSyncPending || (local.pendingRouteSegmentIds?.length || 0) > 0) {
              merged.push(local);
            }
          }

          return from(this.storageService.putAll(this.customerDbName, this.customerStoreName, merged));
        })
      ))
    );
  }

  async createCustomer(input: CustomerCreateInput): Promise<Customer> {
    if (!navigator.onLine) {
      return this.createLocalCustomer(input);
    }

    try {
      const response = await firstValueFrom(
        this.baseApi.post<CustomerCreateResponse>('customers/create', this.corePayload(input))
      );
      if (response.status !== 'success' || !response.customer) {
        throw new Error(response.message || 'Customer creation failed.');
      }

      let customer: Customer = {
        ...response.customer,
        image: input.image || response.customer.image || '',
        imageMimeType: input.imageMimeType || response.customer.imageMimeType || 'image/jpeg',
        pendingSync: false,
        localOnly: false,
      };

      if (input.image) {
        try {
          await this.uploadPhoto(customer, input.image, input.imageMimeType || 'image/jpeg');
          customer = { ...customer, photoSyncPending: false };
        } catch (err) {
          console.warn('Customer created, but shop photo is pending sync.', err);
          customer = { ...customer, photoSyncPending: true };
        }
      }

      await this.upsertCustomer(customer, input.clientCustomerUuid);
      return customer;
    } catch (err: any) {
      // Only network/offline failures are converted into an offline customer.
      if (err?.status === 0 || !navigator.onLine) {
        return this.createLocalCustomer(input);
      }
      throw err;
    }
  }

  async suggestDuplicates(input: CustomerCreateInput): Promise<CustomerDuplicateMatch[]> {
    const localMatches = await this.findLocalDuplicates(input);

    if (navigator.onLine) {
      try {
        const response = await firstValueFrom(
          this.baseApi.post<{ status: string; matches?: CustomerDuplicateMatch[] }>('customers/duplicates', this.corePayload(input))
        );
        const serverMatches = response.matches || [];

        // Never discard strong local matches just because the server returned an empty/partial
        // result. This is important for recently synced/offline route customers.
        const merged = new Map<string, CustomerDuplicateMatch>();
        for (const match of [...serverMatches, ...localMatches]) {
          const key = match.customer.clientCustomerUuid
            ? `uuid:${match.customer.clientCustomerUuid}`
            : `id:${match.customer.id}`;
          const existing = merged.get(key);
          if (!existing || match.score > existing.score) {
            merged.set(key, match);
          }
        }
        return [...merged.values()]
          .sort((a, b) => b.score - a.score)
          .slice(0, 8);
      } catch (err) {
        console.warn('Server duplicate assistance unavailable; using local matches.', err);
      }
    }

    return localMatches;
  }

  async assignCustomerToSegment(customer: Customer, segmentId: number, routeName = '', segmentName = ''): Promise<Customer> {
    const routeIds = new Set<number>(customer.routeIds || (customer.routeId ? [customer.routeId] : []));
    if (routeIds.has(segmentId)) {
      return customer;
    }

    const queueLocally = async (): Promise<Customer> => {
      routeIds.add(segmentId);
      const pending = new Set<number>(customer.pendingRouteSegmentIds || []);
      pending.add(segmentId);
      const updated: Customer = {
        ...customer,
        routeIds: [...routeIds],
        routeId: customer.routeId || segmentId,
        route: customer.route || routeName,
        pendingRouteSegmentIds: [...pending],
      };
      await this.upsertCustomer(updated);
      return updated;
    };

    if (customer.pendingSync || customer.id <= 0 || !navigator.onLine) {
      return queueLocally();
    }

    try {
      await firstValueFrom(this.baseApi.post('customers/assign-route', {
        partnerId: customer.id,
        clientCustomerUuid: customer.clientCustomerUuid || '',
        routeSegmentId: segmentId,
        makePrimary: !customer.routeId,
      }));
      routeIds.add(segmentId);
      const updated: Customer = {
        ...customer,
        routeIds: [...routeIds],
        routeId: customer.routeId || segmentId,
        route: customer.route || routeName,
        routeMemberships: [
          ...(customer.routeMemberships || []),
          { routeId: 0, route: routeName, segmentId, segment: segmentName, isPrimary: !customer.routeId }
        ]
      };
      await this.upsertCustomer(updated);
      return updated;
    } catch (err: any) {
      if (err?.status === 0 || !navigator.onLine) {
        return queueLocally();
      }
      throw err;
    }
  }

  async syncPendingCustomers(): Promise<{ synced: number; failed: number }> {
    const customers = await this.storageService.getAll(this.customerDbName, this.customerStoreName);
    let synced = 0;
    let failed = 0;

    for (const original of [...customers]) {
      let customer = original;
      try {
        if (customer.pendingSync || customer.id <= 0) {
          if (!customer.clientCustomerUuid) {
            failed++;
            continue;
          }

          const response = await firstValueFrom(
            this.baseApi.post<CustomerCreateResponse>('customers/create', this.corePayloadFromCustomer(customer))
          );
          if (response.status !== 'success' || !response.customer) {
            failed++;
            continue;
          }

          customer = {
            ...response.customer,
            image: customer.image || response.customer.image || '',
            imageMimeType: customer.imageMimeType || response.customer.imageMimeType || 'image/jpeg',
            pendingSync: false,
            localOnly: false,
            photoSyncPending: !!customer.image,
            pendingRouteSegmentIds: customer.pendingRouteSegmentIds,
          };
          await this.upsertCustomer(customer, original.clientCustomerUuid);
          synced++;
        }

        if (customer.image && customer.photoSyncPending && customer.id > 0) {
          try {
            await this.uploadPhoto(customer, customer.image, customer.imageMimeType || 'image/jpeg');
            customer = { ...customer, photoSyncPending: false };
            await this.upsertCustomer(customer);
          } catch (photoErr) {
            console.warn('Photo remains pending for customer', customer.name, photoErr);
          }
        }

        if ((customer.pendingRouteSegmentIds?.length || 0) > 0 && customer.id > 0) {
          const remaining: number[] = [];
          for (const segmentId of customer.pendingRouteSegmentIds || []) {
            try {
              await firstValueFrom(this.baseApi.post('customers/assign-route', {
                partnerId: customer.id,
                clientCustomerUuid: customer.clientCustomerUuid || '',
                routeSegmentId: segmentId,
              }));
            } catch (routeErr) {
              remaining.push(segmentId);
            }
          }
          customer = { ...customer, pendingRouteSegmentIds: remaining };
          await this.upsertCustomer(customer);
        }
      } catch (err) {
        console.error('Failed to sync customer', customer.name, err);
        failed++;
      }
    }

    return { synced, failed };
  }

  async findByClientUuid(clientCustomerUuid?: string): Promise<Customer | undefined> {
    if (!clientCustomerUuid) return undefined;
    const customers = await this.storageService.getAll(this.customerDbName, this.customerStoreName);
    return customers.find(c => c.clientCustomerUuid === clientCustomerUuid);
  }

  private async createLocalCustomer(input: CustomerCreateInput): Promise<Customer> {
    const localId = -(Date.now() * 1000 + Math.floor(Math.random() * 1000));
    const segmentId = Number(input.routeSegmentId || 0);
    const customer: Customer = {
      id: localId,
      clientCustomerUuid: input.clientCustomerUuid,
      name: input.name.trim(),
      phone: input.phone || input.mobile || '',
      mobile: input.mobile || '',
      whatsappNumber: input.whatsappNumber || '',
      latitude: input.latitude ?? '',
      longitude: input.longitude ?? '',
      gpsAccuracyM: input.gpsAccuracyM || 0,
      gpsCapturedAt: input.gpsCapturedAt || '',
      gpsSource: input.latitude != null && input.longitude != null ? 'mobile' : '',
      country: '',
      countryId: input.countryId || null,
      state: '',
      stateId: input.stateId || null,
      gstTreatment: input.gstTreatment || 'unregistered',
      tax_id: input.vat || false,
      vat: input.vat || '',
      street: input.street || '',
      street2: input.street2 || '',
      city: input.city || '',
      zip: input.zip || '',
      categories: input.tagNames || [],
      categoryIds: input.tagIds || [],
      image: input.image || '',
      imageMimeType: input.imageMimeType || 'image/jpeg',
      priceList: input.priceListName || '',
      priceListId: Number(input.priceListId || 0),
      outstandingBalance: 0,
      openInvoiceCount: 0,
      route: input.routeName || '',
      routeId: segmentId,
      routeIds: segmentId ? [segmentId] : [],
      routeMemberships: segmentId ? [{
        routeId: Number(input.routeId || 0),
        route: input.routeName || '',
        segmentId,
        segment: input.segmentName || '',
        isPrimary: true,
      }] : [],
      pendingSync: true,
      localOnly: true,
      photoSyncPending: !!input.image,
    };
    await this.upsertCustomer(customer, input.clientCustomerUuid);
    return customer;
  }

  private corePayload(input: CustomerCreateInput): Record<string, unknown> {
    return {
      clientCustomerUuid: input.clientCustomerUuid,
      name: input.name,
      phone: input.phone || '',
      mobile: input.mobile || '',
      whatsappNumber: input.whatsappNumber || '',
      vat: input.vat || '',
      street: input.street || '',
      street2: input.street2 || '',
      city: input.city || '',
      zip: input.zip || '',
      countryId: input.countryId || null,
      stateId: input.stateId || null,
      gstTreatment: input.gstTreatment || 'unregistered',
      tagIds: input.tagIds || [],
      latitude: input.latitude ?? null,
      longitude: input.longitude ?? null,
      gpsAccuracyM: input.gpsAccuracyM ?? 0,
      gpsCapturedAt: input.gpsCapturedAt || null,
      routeId: input.routeId || null,
      routeSegmentId: input.routeSegmentId || null,
      priceListId: input.priceListId || null,
    };
  }

  private corePayloadFromCustomer(customer: Customer): Record<string, unknown> {
    const primarySegment = customer.routeId || customer.routeIds?.[0] || null;
    return {
      clientCustomerUuid: customer.clientCustomerUuid || '',
      name: customer.name,
      phone: customer.phone || '',
      mobile: customer.mobile || '',
      whatsappNumber: customer.whatsappNumber || '',
      vat: customer.vat || customer.tax_id || '',
      street: customer.street || '',
      street2: customer.street2 || '',
      city: customer.city || '',
      zip: customer.zip || '',
      countryId: customer.countryId || null,
      stateId: customer.stateId || null,
      gstTreatment: customer.gstTreatment || 'unregistered',
      tagIds: customer.categoryIds || [],
      latitude: customer.latitude || null,
      longitude: customer.longitude || null,
      gpsAccuracyM: customer.gpsAccuracyM || 0,
      gpsCapturedAt: customer.gpsCapturedAt || null,
      routeSegmentId: primarySegment,
      priceListId: customer.priceListId || null,
    };
  }

  private async uploadPhoto(customer: Customer, image: string, imageMimeType: string): Promise<void> {
    await firstValueFrom(this.baseApi.post('customers/photo', {
      partnerId: customer.id > 0 ? customer.id : null,
      clientCustomerUuid: customer.clientCustomerUuid || '',
      image,
      imageMimeType,
    }));
  }

  private async upsertCustomer(customer: Customer, replaceUuid?: string): Promise<void> {
    const current = await this.storageService.getAll(this.customerDbName, this.customerStoreName);
    const uuid = replaceUuid || customer.clientCustomerUuid;
    const filtered = current.filter(c => {
      if (uuid && c.clientCustomerUuid === uuid) return false;
      return c.id !== customer.id;
    });
    filtered.push(customer);
    await this.storageService.putAll(this.customerDbName, this.customerStoreName, filtered);
  }

  private async findLocalDuplicates(input: CustomerCreateInput): Promise<CustomerDuplicateMatch[]> {
    const customers = await this.storageService.getAll(this.customerDbName, this.customerStoreName);
    const normalizedPhone = this.normalizePhone(input.phone || input.mobile || '');
    const normalizedWhatsapp = this.normalizePhone(input.whatsappNumber || '');
    const normalizedVat = (input.vat || '').replace(/\s+/g, '').toUpperCase();
    const name = input.name.trim().toLowerCase();
    const matches: CustomerDuplicateMatch[] = [];

    for (const customer of customers) {
      let score = 0;
      const reasons: string[] = [];
      const cPhone = this.normalizePhone(customer.phone || customer.mobile || '');
      const cWhatsapp = this.normalizePhone(customer.whatsappNumber || '');
      const cVat = String(customer.vat || customer.tax_id || '').replace(/\s+/g, '').toUpperCase();
      if (normalizedVat && cVat && normalizedVat === cVat) {
        score += 100;
        reasons.push('Same GST/VAT number');
      }
      if (normalizedPhone && cPhone && normalizedPhone === cPhone) {
        score += 35;
        reasons.push('Same phone number');
      }
      if (normalizedWhatsapp && cWhatsapp && normalizedWhatsapp === cWhatsapp) {
        score += 35;
        reasons.push('Same WhatsApp number');
      }
      if (name && customer.name) {
        const a = name.replace(/[^a-z0-9]/g, '');
        const b = customer.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (a && b && (a.includes(b) || b.includes(a))) {
          score += 25;
          reasons.push('Similar name');
        }
      }

      const distanceM = this.distanceMeters(input.latitude, input.longitude, customer.latitude, customer.longitude);
      if (distanceM != null) {
        if (distanceM <= 50) {
          score += 40;
          reasons.push('Within 50 m');
        } else if (distanceM <= 200) {
          score += 25;
          reasons.push('Within 200 m');
        } else if (distanceM <= 1000) {
          score += 10;
          reasons.push('Within 1 km');
        }
      }

      if (score >= 15) {
        matches.push({ score, distanceM, reasons, customer });
      }
    }

    return matches.sort((a, b) => b.score - a.score).slice(0, 8);
  }

  private normalizePhone(value: string): string {
    return value.replace(/\D+/g, '');
  }

  private distanceMeters(lat1: number | null | undefined, lon1: number | null | undefined, lat2: string | number, lon2: string | number): number | null {
    const aLat = Number(lat1);
    const aLon = Number(lon1);
    const bLat = Number(lat2);
    const bLon = Number(lon2);
    if (![aLat, aLon, bLat, bLon].every(Number.isFinite)) return null;
    if (aLat === 0 && aLon === 0) return null;
    const radius = 6371000;
    const toRad = (v: number) => v * Math.PI / 180;
    const dLat = toRad(bLat - aLat);
    const dLon = toRad(bLon - aLon);
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
    return radius * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  }
}
